/**
 * The bag's contents in its standard look (the Prints mockup's bag:
 * docs/mockups/telify-web/dana-wilcher/prints.html, `.bag-*`), in theme tokens,
 * for both places the bag shows: the `/shop/bag` page, and the drawer (its
 * title bar with Close, the list scrolling between it and the subtotal). Draws
 * the behavior half's state and calls its actions; holds no rule.
 */
import { useId } from 'react';

import { partsOf, pieceRoot } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import { PrintPicture } from '../../ui/PrintPicture';
import { contract } from './contract';
import type { ShopBagRuntime } from './useShopBag';
import css from './ShopBag.module.css';

const part = partsOf(contract);

export function BagBody({ state, actions, form, titleId }: ShopBagRuntime & { readonly titleId?: string }) {
  const fallbackId = useId();
  const drawer = form === 'drawer';
  const Title = drawer ? 'h2' : 'h1';
  return (
    <div className={`${css.bag} ${drawer ? css.inDrawer : ''}`} {...(drawer ? {} : pieceRoot(contract))} {...part('bag')}>
      <div className={drawer ? css.top : undefined}>
        <Title id={titleId ?? fallbackId} className={css.title} {...part('title')}>
          Your bag
        </Title>
        {drawer && (
          <button type="button" className={css.round} aria-label="Close the bag" onClick={actions.close} {...part('close')}>
            <Icon name="close" small />
          </button>
        )}
      </div>

      <div className={drawer ? css.scroll : undefined}>
        {state.status === 'empty' && (
          <div className={css.empty} {...part('empty')}>
            <p>Your bag is empty</p>
            <button type="button" className={`${shared.btn} ${shared.btnOutline}`} onClick={actions.keepShopping} {...part('keepShopping')}>
              Keep shopping
            </button>
          </div>
        )}

        {state.lines.length > 0 && (
          <ul className={css.list}>
            {state.lines.map((line) => (
              <li key={line.optionId} className={`${css.item} ${line.available ? '' : css.gone}`} {...part('line')}>
                <span className={css.thumb} {...part('image')}>
                  {line.imageUrl && <PrintPicture url={line.imageUrl} alt="" size={line.size} canvas={line.canvas} />}
                </span>
                <div className={css.info}>
                  <div className={css.name} {...part('name')}>
                    {line.name ?? 'An item'}
                  </div>
                  {line.option && (
                    <div className={css.option} {...part('option')}>
                      {line.option}
                    </div>
                  )}
                  {!line.available && (
                    <p className={css.unavailable} {...part('unavailable')}>
                      {line.soldOut
                        ? line.left
                          ? `Only ${line.left} left.`
                          : 'Sold out.'
                        : 'No longer available, so it won’t be in your order.'}
                    </p>
                  )}
                  {line.was && (
                    <p className={css.changed} {...part('changed')}>
                      The price changed from {line.was}.
                    </p>
                  )}
                  <div className={css.controls}>
                    {/* shop-self-fulfilment D5: with some left, the buyer can lower it to what is left. */}
                    {(line.available || (line.left ?? 0) > 0) && (
                      <span className={css.qty} role="group" aria-label={`How many of ${line.name ?? 'this item'}`} {...part('quantity')}>
                        <button type="button" aria-label="One fewer" onClick={() => actions.setQuantity(line.optionId, line.quantity - 1)}>
                          &minus;
                        </button>
                        <span>{line.quantity}</span>
                        <button
                          type="button"
                          aria-label="One more"
                          disabled={line.quantity >= state.maxQuantity || line.soldOut || line.claimed}
                          onClick={() => actions.setQuantity(line.optionId, line.quantity + 1)}
                        >
                          +
                        </button>
                      </span>
                    )}
                    <button type="button" className={css.remove} onClick={() => actions.remove(line.optionId)} {...part('remove')}>
                      Remove
                    </button>
                  </div>
                </div>
                <div className={css.price} {...part('price')}>
                  {line.total}
                </div>
              </li>
            ))}
          </ul>
        )}

        {state.problem && (
          <p className={css.problem} role="alert" {...part('problem')}>
            {state.problem}
          </p>
        )}
      </div>

      {state.status === 'ready' && (
        <div className={css.foot}>
          <div className={css.subtotal} {...part('subtotal')}>
            <span>Subtotal</span>
            <span>{state.subtotal}</span>
          </div>
          <p className={css.note} {...part('note')}>
            {state.shipping}
          </p>
          <button
            type="button"
            className={`${shared.btn} ${shared.btnPrimary} ${css.wide}`}
            disabled={!state.canCheckout}
            onClick={actions.checkout}
            {...part('checkout', !state.canCheckout && 'disabled')}
          >
            Checkout
          </button>
          <button type="button" className={css.textlink} onClick={actions.keepShopping} {...part('keepShopping')}>
            Keep shopping
          </button>
        </div>
      )}
    </div>
  );
}
